"use client";

import React, { useState } from "react";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import LeftSidebar from "@/components/Layouts/LeftSidebar";
import FloorPlanSection from "@/components/Sections/FloorPlanSection";
import AmenitiesTabs from "@/components/Sections/AmenitiesTabs";
import InteriorSection from "@/components/Sections/InteriorSection";
import FooterContact, { INFO } from "@/components/Layouts/FooterContact";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";

import HeroSection from "@/components/Sections/HeroSection";
import HighlightsFormSection from "@/components/Sections/HighlightsFormSection";
import SalesPolicy from "@/components/Sections/SalesPolicy";
import ProjectOverview, {
  ApartmentTypes,
} from "@/components/Sections/ProjectOverview";
import NewsSection from "@/components/Sections/NewsSection";
import ProductDetailModal from "@/components/ProductDetailModal";
import RegisterModal from "@/components/RegisterModal";
import WhyChooseSection from "@/components/Sections/WhyChooseSection";
import GalleryModal from "@/components/Layouts/components/GalleryModal";
import BusinessSlider from "@/components/Sections/BusinessSlider";
import { PROJECT_INFO } from "@/types/projectConsts";
import { getLenis } from "@/components/SmoothScrollProvider";
import MapSection from "@/components/Sections/MapSection";
import ScrollingGoldLine from "@/components/ScrollingGoldLine";
import {
  parseStructuredListFromHTML,
  stripHTML,
  parseHtmlTextToJson,
  sanitizeHTMLKeepStyleWithFont,
} from "@/utils/stripHTML";

interface HomeProps {
  seo: any;
  data: any;
  locale: string;
}

export default function HomeClient({ data, locale = "vi" }: HomeProps) {
  // Flatten all addon lists to make searching robust across all CMS slots
  const allAddOns = [
    ...(Array.isArray(data?.add_on_data) ? data.add_on_data : []),
  ];

  //#region data map

  // Helper to find add-on by title (ID)
  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getSectionIndex = (key: string, defaultVal: number): number => {
    const addon = getAddOn(key);
    if (!addon) return defaultVal;
    const stt = addon.stt;
    if (stt === undefined || stt === null || stt === "") {
      return defaultVal;
    }
    const val = Number(stt);
    return isNaN(val) ? defaultVal : val;
  };
  const getAddOn2 = (key: string) => {
    return (
      data?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };
  const getAddOn3 = (key: string) => {
    return (
      data?.add_on_data_3?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  // Dynamically configure global PROJECT_INFO properties from project_info JSON API data
  const projectInfo = data?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.hotline) PROJECT_INFO.hotline = parsed.hotline;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.messenger) {
        PROJECT_INFO.messenger = parsed.messenger;
        PROJECT_INFO.fb = parsed.messenger;
      } else if (parsed.fb) {
        PROJECT_INFO.messenger = parsed.fb;
        PROJECT_INFO.fb = parsed.fb;
      }
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }
  // Helper to check if a section is shared (visible)
  const isSectionShared = (key: string, defaultVal = true): boolean => {
    const item = getAddOn(key);
    if (!item) return defaultVal;
    return (
      item.shared !== false &&
      item.shared !== "false" &&
      item.shared !== 0 &&
      item.shared !== "0"
    );
  };

  // Helper to consolidate images from main addon and secondary addons with same title
  const getAddOnImages = (key: string): string[] => {
    const matching = allAddOns.filter(
      (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
    );
    const images: string[] = [];
    matching.forEach((item: any) => {
      const srcList = [
        item?.picture,
        item?.picture_2,
        item?.picture_3,
        item?.picture_4,
        item?.picture_5,
        item?.picture_6,
      ];
      srcList.forEach((src) => {
        if (src) {
          images.push(appendImageUrlFromAPI({ src, size: "2k" }));
        }
      });
    });
    return images;
  };

  //#endregion

  //#region States
  const [selectedProduct, setSelectedProduct] = useState<string>("Đất nền");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedOverviewProduct, setSelectedOverviewProduct] = useState<
    any | null
  >(null);
  const [lightboxMedia, setLightboxMedia] = useState<any[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [apartmentModalOpen, setApartmentModalOpen] = useState(false);
  const [selectedApartmentProduct, setSelectedApartmentProduct] = useState("");

  const openLightbox = (mediaArray: any[], index: number = 0) => {
    setLightboxMedia(
      mediaArray.map((src) =>
        typeof src === "string" ? { type: "image", src } : src,
      ),
    );
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const IMAGE4 = [
    appendImageUrlFromAPI({ src: data?.add_on_data?.[0]?.picture, size: "2k" }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_2,
      size: "2k",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_3,
      size: "2k",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_4,
      size: "2k",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_5,
      size: "2k",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_6,
      size: "2k",
    }),
  ];

  //#endregion

  //#region hero data
  const heroItem = getAddOn("herosection");
  const heroFormAddon = data?.add_on_data_2?.find(
    (item: any) => item?.title?.toLowerCase() === "hero-form",
  );
  const showHeroForm = heroFormAddon?.shared !== false;

  const highlightFormAddon = data?.add_on_data_2?.find(
    (item: any) => item?.title?.toLowerCase() === "highlight-form",
  );
  const showHighlightForm = highlightFormAddon?.shared !== false;
  const heroImages =
    getAddOnImages("herosection")[0] || "/images/hero_skyline.png";
  const heroTitle = heroItem?.summary || "KIẾN TẠO KHÔNG GIAN SỐNG HOÀN MỸ";
  const heroDes =
    heroItem?.content_2 ||
    "Tọa lạc tại vị trí vàng đắc địa, dự án mang đến trải nghiệm sống hoàn mỹ giữa thiên nhiên sinh thái trong lành cùng chuỗi tiện ích đẳng cấp, kiến tạo không gian sống xứng tầm vị thế chủ nhân.";
  let heroFormTitle = "ĐĂNG KÝ TƯ VẤN NGAY";
  let heroFormSubtitle = "Nhận thông tin mặt bằng & bảng giá mới nhất";
  let heroFormLayoutTypeTitle = "Sản phẩm quan tâm";
  let heroApptOptions: Array<{ label: string; value: string }> = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ];

  try {
    const rawContent = heroItem?.content
      ? stripHTML(heroItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed.heroFormTitle) heroFormTitle = parsed.heroFormTitle;
      if (parsed.heroFormSubtitle) heroFormSubtitle = parsed.heroFormSubtitle;
      if (parsed.heroFormLayoutTypeTitle)
        heroFormLayoutTypeTitle = parsed.heroFormLayoutTypeTitle;
      if (parsed.heroApptOptions && Array.isArray(parsed.heroApptOptions)) {
        heroApptOptions = parsed.heroApptOptions.map((item: any) => ({
          label: item.label || item.title || "Căn hộ",
          value: item.value || item.title || "Studio",
        }));
      }
    }
  } catch (e) {
    console.error("Failed to parse hero options:", e);
  }

  if (heroItem?.content_3) {
    const cleanContent3 = stripHTML(heroItem.content_3);
    const parts = cleanContent3.split("***").map((p) => p.trim());
    if (parts[0]) heroFormTitle = parts[0];
    if (parts[1]) heroFormSubtitle = parts[1];
  }
  //#endregion

  //#region project overview data
  const overviewItem = getAddOn("project-overview");
  const apartmentTypes = getAddOn("apartment-types");
  let overviewTitle = "TỔNG QUAN DỰ ÁN";
  let overviewSubtitle = "Thông tin chi tiết";
  let overviewTableHeader = "Thông tin tổng quan dự án";
  if (overviewItem?.summary) {
    const cleanSummary = stripHTML(overviewItem.summary);
    const parts = cleanSummary.split("***").map((p) => p.trim());
    overviewTitle = parts[0] || overviewTitle;
    overviewSubtitle = parts[2] || overviewSubtitle;
    overviewTableHeader = parts[1] || overviewTableHeader;
  }
  const overviewDes = overviewItem?.content_2 || "";
  const overviewVideoUrl = overviewItem?.content_3;
  const overviewImages = getAddOnImages("project-overview");
  const overviewVideoCover = overviewImages[0] || "/images/noi_that_1.png";
  const overviewGallery =
    overviewImages.length > 1 ? overviewImages.slice(1) : IMAGE4;

  const overviewLayoutsSubtitle = apartmentTypes?.content_2;
  const overviewLayoutsTitle = apartmentTypes?.summary;
  const parsedLayouts = (
    Array.isArray(data?.add_on_data_2) ? data.add_on_data_2 : []
  )
    .filter(
      (item: any) =>
        item?.shared !== false &&
        item?.shared !== "false" &&
        item?.shared !== 0 &&
        item?.shared !== "0" &&
        item?.title?.toLowerCase() === "apartment-type",
    )
    .map((item: any) => {
      let extraData: any = {};
      const rawContent = item?.content ? stripHTML(item.content) : "";
      try {
        const trimmed = rawContent.trim();
        if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
          try {
            extraData = JSON.parse(trimmed);
          } catch (e) {
            try {
              extraData = new Function(`return (${trimmed})`)();
            } catch (err) {
              console.warn("Failed to evaluate JSON fallback:", err);
            }
          }
        }
      } catch (e) {
        console.error("Failed to parse apartment layout specs JSON:", e);
      }
      return {
        title: item?.summary || item?.title || "Căn hộ",
        image:
          appendImageUrlFromAPI({ src: item?.picture, size: "2k" }) ||
          "/images/hero_skyline.png",
        desc: item?.content_2 || "",
        specs: Array.isArray(extraData?.specs) ? extraData.specs : [],
        beds: extraData?.beds ?? 1,
        baths: extraData?.baths ?? 1,
        area: extraData?.area ?? "0 m²",
        type: extraData?.type ?? "APARTMENT",
        price: extraData?.price ?? "Liên hệ",
        badgeGreen: extraData?.badgeGreen ?? "NỔI BẬT",
        badgeDark: extraData?.badgeDark ?? "MỞ BÁN",
      };
    });

  const overviewApartmentLayouts =
    parsedLayouts.length > 0 ? parsedLayouts : undefined;

  let overviewSpecs: Array<{ label: string; value: string }> = [];
  try {
    const rawContent = overviewItem?.content
      ? stripHTML(overviewItem.content).trim()
      : "";

    const trimmed = rawContent.trim();
    if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
      let parsed = null;
      try {
        parsed = JSON.parse(trimmed);
      } catch (err) {
        try {
          parsed = new Function(`return (${trimmed})`)();
        } catch (e) {
          console.warn("Failed to evaluate overview specs JSON fallback:", e);
        }
      }
      if (parsed && Array.isArray(parsed)) {
        overviewSpecs = parsed.map((item: any) => ({
          label: item.label || item.title || "Căn hộ",
          value: item.value || item.title || "Studio",
        }));
      }
    }
  } catch (e) {
    console.error("Failed to parse overview specs:", e);
  }

  //#endregion

  //#region map section

  const mapSectionItem_1 = getAddOn("map-section-1");
  const mapSectionItem_2 = getAddOn("map-section-2");

  const mapTitle = mapSectionItem_1?.summary;
  const mapSubtitle = mapSectionItem_1?.content_3;

  const mapDescription = mapSectionItem_1?.content_2;
  const mapSubDescription = sanitizeHTMLKeepStyleWithFont(
    mapSectionItem_1?.content,
  );

  const mapImage =
    getAddOnImages("map-section-1")[0] || "/images/map_dragonhome.png";

  const trainTitle = mapSectionItem_2?.summary;
  const trainSubtitle = mapSectionItem_2?.content_3;
  const rawContent = parseHtmlTextToJson(mapSectionItem_2?.content) || "[]";

  const trainImages = getAddOnImages("map-section-2");

  let infrastructureRoutes: Array<{
    title: string;
    desc: string;
    image: string;
  }> = [];

  try {
    if (Array.isArray(rawContent)) {
      infrastructureRoutes = rawContent.map((item: any, idx: number) => ({
        title: item.title || "",
        desc: item.desc || "",
        image: trainImages[idx] || item.image || "/images/hero_skyline.png",
      }));
    }
  } catch (e) {
    console.error("Failed to parse infrastructure routes:", e);
  }

  //#endregion

  //#region interior groups data
  const interiorGeneral = getAddOn("interior-section");
  const interiorItems = data?.add_on_data_2?.filter(
    (item: any) => item?.title?.toLowerCase() === "interior",
  );

  const interiorGroups = interiorItems.map((item: any, idx: number) => {
    const itemImages = [
      item?.picture,
      item?.picture_2,
      item?.picture_3,
      item?.picture_4,
      item?.picture_5,
      item?.picture_6,
    ]
      .filter(Boolean)
      .map((src) => appendImageUrlFromAPI({ src, size: "2k" }));

    return {
      id: item.id || `interior-group-${idx}`,
      title: item.summary || `Thiết kế nội thất ${idx + 1}`,
      images: itemImages,
    };
  });
  //#endregion

  //#region amenities tabs data

  const amenitiesSection = getAddOn("amenities");
  const amenityTitle = amenitiesSection?.summary;
  const amenitySubtitle = amenitiesSection?.content_3;
  const amenity_1 = getAddOn2("amenity-1");
  const amenity_2 = getAddOn2("amenity-2");
  const amenity_3 = getAddOn2("amenity-3");
  const amenity_4 = getAddOn2("amenity-4");

  const amenitiesList = [amenity_1, amenity_2, amenity_3, amenity_4]
    .filter(Boolean)
    .map((item: any, idx: number) => {
      const images = [
        appendImageUrlFromAPI({ src: item?.picture_2, size: "2k" }),
        appendImageUrlFromAPI({ src: item?.picture_3, size: "2k" }),
        appendImageUrlFromAPI({ src: item?.picture_4, size: "2k" }),
        appendImageUrlFromAPI({ src: item?.picture_5, size: "2k" }),
      ].filter(Boolean) as string[];

      let parsedTitles: Array<{ title?: string }> = [];
      if (item?.content) {
        parsedTitles = parseHtmlTextToJson<{ title?: string }>(item.content);
      }

      const imageTitles = images.map((_, i) => {
        return parsedTitles[i]?.title || item.summary || `Tiện ích ${i + 1}`;
      });

      return {
        id: item.title || `amenity-${idx + 1}`,
        title: item.summary || "Tiện ích",
        subtitle: item.content_3 || "Đặc quyền cư dân",
        icon: appendImageUrlFromAPI({ src: item?.picture, size: "2k" }) || "",
        images: images,
        imageTitles: imageTitles,
      };
    });
  //#endregion

  //#region floor plan slides data

  const projectFloor = getAddOn("project-floor");
  const projectFloorImages = getAddOnImages("project-floor");

  const parsedFloorPlans = projectFloor?.content
    ? parseHtmlTextToJson<{ title: string; description: string }>(
        projectFloor.content,
      )
    : [];

  const floorPlanSlides =
    parsedFloorPlans.length > 0
      ? parsedFloorPlans.map((item: any, idx: number) => ({
          image: projectFloorImages[idx] || "/images/phoi_canh_3.png",
          title: item.title || "Mặt bằng phân khu",
          description:
            item.description || "Khu đô thị Dragon Home quy hoạch đồng bộ.",
        }))
      : [];
  //#endregion

  //#region policy data
  const policy = getAddOn("policy-section");

  const parsedPolicies = policy?.content
    ? parseHtmlTextToJson<{ value: string; label: string; desc: string }>(
        policy.content,
      )
    : [];

  const policyImages = [
    policy?.picture,
    policy?.picture_2,
    policy?.picture_3,
    policy?.picture_4,
    policy?.picture_5,
  ]
    .filter(Boolean)
    .map((src) => appendImageUrlFromAPI({ src, size: "xl" }));

  const policyItems = parsedPolicies.map((item: any, idx: number) => ({
    value: item.value || "",
    label: item.label || "",
    desc: item.desc || "",
    icon: policyImages[idx] || "",
  }));
  //#endregion

  //#region highlight data
  const highlight = getAddOn("highlight-section");

  const parsedHighlights = highlight?.content
    ? parseHtmlTextToJson<{ title: string; description: string; href: string }>(
        highlight.content,
      )
    : [];

  const highlightImages = [
    highlight?.picture,
    highlight?.picture_2,
    highlight?.picture_3,
    highlight?.picture_4,
    highlight?.picture_5,
    highlight?.picture_6,
  ]
    .filter(Boolean)
    .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

  const highlightItems = parsedHighlights.map((item: any, idx: number) => ({
    title: item.title || "",
    description: item.description || "",
    href: item.href || "",
    icon: highlightImages[idx] || "",
  }));
  //#endregion

  //#region progress slides data

  const progressGeneral = getAddOn("progress-section");
  const progressItems = data?.add_on_data_2.filter((item: any) =>
    item?.title?.toLowerCase().startsWith("progress-"),
  );

  const progressSlides =
    progressItems && progressItems.length > 0
      ? progressItems.map((item: any, index: number) => {
          const mainImg =
            appendImageUrlFromAPI({ src: item.picture, size: "xl" }) ||
            `/images/phoi_canh_${(index % 4) + 1}.png`;
          return {
            id: item.id || `tien-do-${index + 1}`,
            title: item.summary || "Tiến độ dự án",
            desc: item.content
              ? stripHTML(item.content).trim()
              : "Cam kết bàn giao đúng tiến độ, đảm bảo chất lượng kỹ thuật cao nhất.",
            image: mainImg,
            _raw: {
              add_on_data: [
                {
                  picture: item.picture,
                  picture_2: item.picture_2,
                  picture_3: item.picture_3,
                  picture_4: item.picture_4,
                  picture_5: item.picture_5,
                  picture_6: item.picture_6,
                },
              ],
            },
          };
        })
      : [];
  //#endregion

  //#region news section data
  const newsSection = getAddOn("news-section");
  //#endregion

  //#region why choose section data
  const whyChooseSection = getAddOn("why-choose-section");
  const whyChooseTitle = whyChooseSection?.summary || undefined;
  const whyChooseSubtitle = whyChooseSection?.content_3 || undefined;
  const whyChooseDescription = whyChooseSection?.content_2
    ? stripHTML(whyChooseSection.content_2).trim()
    : undefined;
  const whyChooseHighlightsList = whyChooseSection?.content
    ? parseHtmlTextToJson<{ value: string; label: string; icon?: string }>(
        whyChooseSection.content,
      ).map((item, idx) => {
        let picSrc = "";
        if (idx === 0 && whyChooseSection.picture)
          picSrc = whyChooseSection.picture;
        else if (idx === 1 && whyChooseSection.picture_2)
          picSrc = whyChooseSection.picture_2;
        else if (idx === 2 && whyChooseSection.picture_3)
          picSrc = whyChooseSection.picture_3;
        else if (idx === 3 && whyChooseSection.picture_4)
          picSrc = whyChooseSection.picture_4;
        else if (idx === 4 && whyChooseSection.picture_5)
          picSrc = whyChooseSection.picture_5;
        else if (idx === 5 && whyChooseSection.picture_6)
          picSrc = whyChooseSection.picture_6;

        return {
          ...item,
          icon: picSrc
            ? appendImageUrlFromAPI({ src: picSrc, size: "l" })
            : undefined,
        };
      })
    : undefined;
  //#endregion

  //#region faq section data
  const faqSection = getAddOn("faq-section");
  const parsedFaqs = faqSection?.content
    ? parseHtmlTextToJson<{ question: string; answer: string }>(
        faqSection.content,
      )
    : [];
  //#endregion

  //#region map regions and footer data

  const footerItem = getAddOn("footer");

  const partnersSection = getAddOn2("footer-partners");
  const partnersNames = partnersSection?.content
    ? stripHTML(partnersSection.content)
        .split("***")
        .map((s) => s.trim())
    : [];

  const partnersLogos = [
    partnersSection?.picture,
    partnersSection?.picture_2,
    partnersSection?.picture_3,
    partnersSection?.picture_4,
    partnersSection?.picture_5,
    partnersSection?.picture_6,
  ]
    .filter(Boolean)
    .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

  const partnersList = partnersNames.map((name, idx) => ({
    name,
    logo: partnersLogos[idx] || "/logos/logo_with_name.png",
  }));

  const footerJsonData = stripHTML(footerItem?.content)
    ? parseHtmlTextToJson<any>(footerItem.content)
    : null;

  const resolveTemplateStr = (str?: string) => {
    if (!str) return "";
    return str
      .replace("[PROJECT_INFO.hotline]", PROJECT_INFO.hotline)
      .replace("[PROJECT_INFO.email]", PROJECT_INFO.email)
      .replace("[PROJECT_INFO.website]", PROJECT_INFO.website);
  };

  const footerData = (footerJsonData?.[0] || footerJsonData) as any;
  const footerInfoPart = footerData?.footerInfo;

  const FOOTERINFO: INFO = {
    title: footerInfoPart?.title || "Liên hệ tư vấn",
    description1:
      footerInfoPart?.description1 ||
      "Dragon Home – Căn hộ chung cư hiện đại chuẩn sống mới tại Long An.",
    title2: footerInfoPart?.title2 || "Địa chỉ",
    description2:
      footerInfoPart?.description2 ||
      "Khu dân cư Ngọc Long, Đức Hòa Đông, Đức Hòa, Long An, nay là Mỹ Hạnh, Tây Ninh.",
    title3: footerInfoPart?.title3 || "liên hệ",
    description3:
      footerInfoPart?.description3 ||
      "Quý khách hàng vui lòng để lại thông tin, Dragon Home sẽ liên hệ và hỗ trợ trong thời gian sớm nhất.",
    phone:
      resolveTemplateStr(footerInfoPart?.phone) ||
      `Hotline: ${PROJECT_INFO.hotline}`,
    mail:
      resolveTemplateStr(footerInfoPart?.mail) ||
      `Email: ${PROJECT_INFO.email}`,
    webUrl:
      resolveTemplateStr(footerInfoPart?.webUrl) ||
      `Website: ${PROJECT_INFO.website}`,
    fb: footerInfoPart?.fb || "https://www.facebook.com/orapark/",
    youtube:
      footerInfoPart?.youtube ||
      "https://www.youtube.com/@OneVisionGroup-VietNam",
  };

  const footerReasons = footerData?.footerReasons;

  const brandLogos = [
    {
      name: footerData?.brandLogo2Text || "Chủ đầu tư",
      logo:
        appendImageUrlFromAPI({ src: footerItem?.picture_3, size: "s" }) ||
        "/logos/logo_orapark.png",
    },
    {
      name: footerData?.brandLogo3Text || "Đơn vị phát triển",
      logo:
        appendImageUrlFromAPI({ src: footerItem?.picture_4, size: "s" }) ||
        "/logos/logo.png",
    },
  ].filter((item) => Boolean(item.logo));
  const footerRegisterPart = footerData?.footerRegister;

  const footerRegister = {
    title: footerRegisterPart?.title || "ĐĂNG KÝ NHẬN THÔNG TIN ƯU TIÊN",
    subtitle: footerRegisterPart?.subtitle || "",
    benefits: footerRegisterPart?.benefits || [],
    calloutTitle:
      footerRegisterPart?.calloutTitle || "NHẬN THÔNG TIN NGAY HÔM NAY",
    calloutDesc:
      footerRegisterPart?.calloutDesc ||
      "Booking chỉ 20 triệu • Giá dự kiến từ 25 triệu/m² • Thanh toán đợt đầu chỉ 20%",
    hotline:
      resolveTemplateStr(footerRegisterPart?.hotline) ||
      `Hotline: ${PROJECT_INFO.hotline}`,
    note:
      footerRegisterPart?.note ||
      "Đăng ký ngay để trở thành những khách hàng đầu tiên lựa chọn vị trí đẹp và chính sách tốt nhất của Dragon Home.",
  };

  const footerBgImage =
    appendImageUrlFromAPI({ src: footerItem?.picture, size: "xl" }) ||
    "/images/phoi_canh_3.png";

  const footerPartnersLabel = footerData?.partnersLabel || "ĐỐI TÁC DỰ ÁN";
  const footerAddressLabel = footerInfoPart?.title2 || "ĐỊA CHỈ DỰ ÁN";
  const footerContactLabel = footerInfoPart?.title3 || "THÔNG TIN LIÊN HỆ";
  const footerReasonsTitle = footerData?.reasonsTitle || "ĐẶC QUYỀN SỞ HỮU";
  const footerReasonsSubtitle =
    footerData?.reasonsSubtitle || "VÌ SAO NÊN XUỐNG TIỀN NGAY HÔM NAY?";
  const footerBenefitsTitle =
    footerRegisterPart?.benefitsTitle ||
    footerData?.benefitsTitle ||
    "Đăng ký ngay hôm nay để nhận:";
  const footerCopyrightText =
    footerData?.copyrightText ||
    footerData?.copyright ||
    "©2026 Copyright by ZenGroup. All rights reserved.";
  //#endregion

  /* =========================================================================
     MAPPING CÁC SECTION VỚI SCROLLHEADER (Scroll Assist Sidebar):
     - #banner-anchor         -> 01: Trang chủ (Banner & Đăng ký báo giá)
     - #gioi-thieu            -> 02: Giới thiệu (Tổng quan dự án)
     - #loai-hinh-san-pham    -> 03: Sản phẩm (Các phân khu/loại hình)
     - #tien-ich              -> 04: Tiện ích (Cụm tiện ích & Tiện ích All-in-One)
     - #mat-bang              -> 05: Mặt bằng (Sơ đồ phân lô & liên kết vùng)
     - #chinh-sach-ban-hang   -> 06: Chính sách (Chính sách bán hàng ưu đãi)
     - #vi-tri                -> 07: Vị trí (Google Maps)
     - #tin-tuc               -> 08: Tin tức (Tin tức & Sự kiện)
     - #contact               -> 09: Liên hệ (Footer / Đăng ký tư vấn)
     ========================================================================= */

  // Define all dynamic sections for sorting based on database index
  const sectionsList = [
    {
      id: "herosection",
      index: getSectionIndex("herosection", 0),
      render: () =>
        isSectionShared("herosection") && (
          <HeroSection
            selectedProduct={selectedProduct}
            imageUrl={heroImages}
            title={heroTitle}
            description={heroDes}
            formTitle={heroFormTitle}
            formSubtitle={heroFormSubtitle}
            formLayoutTypeTitle={heroFormLayoutTypeTitle}
            apptOptions={heroApptOptions}
            showForm={showHeroForm}
          />
        ),
    },
    {
      id: "highlight-section",
      index: getSectionIndex("highlight-section", 1),
      render: () =>
        isSectionShared("highlight-section") && (
          <HighlightsFormSection
            defaultProduct={selectedProduct}
            highlights={highlightItems}
            showForm={showHighlightForm}
            title={highlight?.summary || "Đặc điểm nổi bật"}
          />
        ),
    },
    {
      id: "project-overview",
      index: getSectionIndex("project-overview", 2),
      render: () => (
        <div
          id="gioi-thieu"
          className="border-t border-black/5"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 1) 100%)",
          }}
        >
          {isSectionShared("project-overview") && (
            <ProjectOverview
              title={overviewTitle}
              description={overviewDes}
              subtitle={overviewSubtitle}
              tableHeader={overviewTableHeader}
              specs={overviewSpecs}
              videoUrl={overviewVideoUrl}
              videoCoverImage={overviewVideoCover}
              galleryImages={overviewGallery}
              onImageClick={(idx) => {
                const mediaList = [];
                if (overviewVideoUrl) {
                  let embedUrl = overviewVideoUrl;
                  if (overviewVideoUrl.includes("youtube.com/watch?v=")) {
                    embedUrl = overviewVideoUrl.replace(
                      "youtube.com/watch?v=",
                      "youtube.com/embed/",
                    );
                  } else if (overviewVideoUrl.includes("youtu.be/")) {
                    embedUrl = overviewVideoUrl.replace(
                      "youtu.be/",
                      "youtube.com/embed/",
                    );
                  }
                  mediaList.push({
                    type: "video",
                    src: embedUrl,
                    title: overviewTitle || "Video giới thiệu dự án",
                  });
                }
                overviewGallery.forEach((src) => {
                  mediaList.push({ type: "image", src });
                });
                setLightboxMedia(mediaList);
                setLightboxIndex(overviewVideoUrl ? idx + 1 : idx);
                setLightboxOpen(true);
              }}
            />
          )}
          {isSectionShared("apartment-types") && (
            <ApartmentTypes
              layoutsSubtitle={overviewLayoutsSubtitle}
              layoutsTitle={overviewLayoutsTitle}
              apartmentLayouts={overviewApartmentLayouts}
              onProductClick={(prod) => setSelectedOverviewProduct(prod)}
              onConsultClick={(title) => {
                setSelectedApartmentProduct(title);
                setApartmentModalOpen(true);
              }}
            />
          )}
        </div>
      ),
    },
    {
      id: "map-section",
      index: getSectionIndex("map-section", 3),
      render: () =>
        isSectionShared("map-section") && (
          <div id="vi-tri">
            <MapSection
              image={mapImage}
              mapDescription={mapDescription}
              mapSubDescription={mapSubDescription}
              mapTitle={mapTitle}
              mapSubtitle={mapSubtitle}
              trainTitle={trainTitle}
              trainSubtitle={trainSubtitle}
              infrastructureRoutes={infrastructureRoutes}
              onImageClick={() => openLightbox([mapImage])}
            />
          </div>
        ),
    },
    {
      id: "why-choose-section",
      index: getSectionIndex("why-choose-section", 4),
      render: () =>
        isSectionShared("why-choose-section", true) && (
          <WhyChooseSection
            apptOptions={heroApptOptions}
            title={whyChooseTitle}
            subtitle={whyChooseSubtitle}
            description={whyChooseDescription}
            highlightsList={whyChooseHighlightsList}
          />
        ),
    },
    {
      id: "amenities-tabs",
      index: getSectionIndex("amenities-tabs", 5),
      render: () =>
        isSectionShared("amenities-tabs") && (
          <div id="tien-ich">
            <AmenitiesTabs
              tabsList={amenitiesList}
              subTitle={amenitySubtitle}
              title={amenityTitle}
              onImageClick={(tabIdx, imgIdx) => {
                const tab = amenitiesList[tabIdx];
                if (tab) {
                  const mediaItems = tab.images.map((src: any, i: number) => ({
                    type: "image",
                    src,
                    title: tab.imageTitles?.[i] || tab.title,
                  }));
                  openLightbox(mediaItems, imgIdx);
                }
              }}
            />
          </div>
        ),
    },
    {
      id: "project-floor",
      index: getSectionIndex("project-floor", 6),
      render: () =>
        isSectionShared("project-floor") && (
          <div id="floor">
            <FloorPlanSection
              slides={floorPlanSlides}
              title={projectFloor?.summary || "Mặt bằng dự án"}
            />
          </div>
        ),
    },
    {
      id: "interior",
      index: getSectionIndex("interior", 7),
      render: () =>
        isSectionShared("interior") && (
          <InteriorSection
            groups={interiorGroups}
            title={interiorGeneral?.summary}
            subTitle={interiorGeneral?.content_3}
            des={stripHTML(interiorGeneral?.content)}
            onGroupClick={(idx) => {
              const group = interiorGroups[idx];
              if (group) {
                const mediaItems = group.images.map((src: any, i: number) => ({
                  type: "image",
                  src,
                  title: `${group.title} - Thiết kế mẫu ${i + 1}`,
                }));
                openLightbox(mediaItems, 0);
              }
            }}
          />
        ),
    },
    {
      id: "policy-section",
      index: getSectionIndex("policy-section", 8),
      render: () =>
        isSectionShared("policy-section") && (
          <SalesPolicy
            policies={policyItems}
            title={policy?.summary}
            subTitle={policy?.content_3}
            onDownloadSuccess={() => {}}
          />
        ),
    },
    {
      id: "progress-section",
      index:
        getSectionIndex("progress-section", 9) !== 9
          ? getSectionIndex("progress-section", 9)
          : getSectionIndex("faq-section", 9),
      render: () =>
        (isSectionShared("progress-section") ||
          isSectionShared("faq-section")) && (
          <div id="process">
            <BusinessSlider
              businessFields={
                isSectionShared("progress-section") ? progressSlides : []
              }
              title={progressGeneral?.summary}
              desc={stripHTML(progressGeneral?.content)}
              faqList={isSectionShared("faq-section") ? parsedFaqs : []}
              faqTitle={faqSection?.summary}
              faqSubTitle={faqSection?.content_3}
            />
          </div>
        ),
    },
    {
      id: "news-section",
      index: getSectionIndex("news-section", 10),
      render: () =>
        isSectionShared("news-section") && (
          <NewsSection
            locale={locale}
            title={newsSection?.summary}
            subTitle={newsSection?.content_3}
          />
        ),
    },
  ];

  // Sort sections list by index ascending
  const sortedSections = [...sectionsList].sort((a, b) => a.index - b.index);

  return (
    <div className="relative w-full overflow-x-hidden">
      {/* <ScrollingGoldLine /> */}
      {/* <PopupSlider IMAGES={IMAGES} /> */}
      <ScrollHeader
        fb={FOOTERINFO.fb}
        mail={FOOTERINFO.mail}
        visibleSections={{
          "#banner-anchor": isSectionShared("herosection"),
          "#gioi-thieu": isSectionShared("project-overview"),
          "#vi-tri": isSectionShared("map-section"),
          "#tien-ich": isSectionShared("amenities-tabs"),
          "#floor": isSectionShared("project-floor"),
          "#chinh-sach-ban-hang": isSectionShared("policy-section"),
          "#process": isSectionShared("progress-section"),
          "#tin-tuc": isSectionShared("news-section"),
        }}
        apptOptions={heroApptOptions}
        formTitle={heroFormTitle}
        formSubtitle={heroFormSubtitle}
        formLayoutTypeTitle={heroFormLayoutTypeTitle}
        logoUrl={
          appendImageUrlFromAPI({ src: data?.picture_2, size: "xl" }) ||
          "/logos/logo_transparent.png"
        }
      />
      <LeftSidebar />

      {/* ── SECTION 01: TRANG CHỦ ── */}
      <div id="banner-anchor" className="h-[1px] w-full" />

      {/* Render sorted dynamic sections */}
      {sortedSections.map((sec) => (
        <React.Fragment key={sec.id}>{sec.render()}</React.Fragment>
      ))}

      {/* ── SECTION 08: LIÊN HỆ ── */}
      <div id="dang-ky-bao-gia">
        <FooterContact
          INFO={FOOTERINFO}
          reasonsList={footerReasons}
          registerInfo={footerRegister}
          bgImageUrl={footerBgImage}
          apptOptions={heroApptOptions}
          formTitle={heroFormTitle}
          formSubtitle={heroFormSubtitle}
          partners={partnersList}
          partnersLabel={footerPartnersLabel}
          brandLogos={brandLogos}
          addressLabel={footerAddressLabel}
          contactLabel={footerContactLabel}
          reasonsTitle={footerReasonsTitle}
          reasonsSubtitle={footerReasonsSubtitle}
          benefitsTitle={footerBenefitsTitle}
          copyrightText={footerCopyrightText}
          logoUrl={
            appendImageUrlFromAPI({ src: footerItem?.picture_2, size: "xl" }) ||
            "/logos/logo_transparent.png"
          }
        />
      </div>

      {/* Lightbox modal */}
      <GalleryModal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        media={lightboxMedia}
        initialIndex={lightboxIndex}
      />

      {/* Product detail modal */}
      <ProductDetailModal
        open={!!selectedOverviewProduct}
        onClose={() => setSelectedOverviewProduct(null)}
        product={selectedOverviewProduct}
      />

      {/* Dynamic consultation register modal */}
      <RegisterModal
        open={apartmentModalOpen}
        onClose={() => setApartmentModalOpen(false)}
        defaultProduct={selectedApartmentProduct}
        apptOptions={heroApptOptions}
        formTitle={heroFormTitle}
        formSubtitle={heroFormSubtitle}
        formLayoutTypeTitle={heroFormLayoutTypeTitle}
      />
    </div>
  );
}
